import { useEffect, useMemo, useState } from "react";
import { useCalls } from "@/CallsContext";
import { useGoals } from "@/GoalsContext";
import MonthlyGoalPanel from "@/components/MonthlyGoalPanel";
import { currencySymbol, money } from "@/lib/callStats";
import { goalHistory, getMonthlyGoal, monthGoalSnapshot, setMonthlyGoal } from "@/lib/goals";
import { monthKey } from "@/lib/period";

export default function GoalsPage() {
  const { calls } = useCalls();
  const { version, saveGoal } = useGoals();
  const [month, setMonth] = useState(() => monthKey(new Date()));
  const [draft, setDraft] = useState(() => {
    const amount = getMonthlyGoal(monthKey(new Date()));
    return amount > 0 ? String(amount) : "";
  });
  const [saved, setSaved] = useState(0);
  const symbol = currencySymbol(calls);

  useEffect(() => {
    const amount = getMonthlyGoal(month);
    setDraft(amount > 0 ? String(amount) : "");
  }, [month, saved, version]);

  const snapshot = useMemo(
    () => monthGoalSnapshot(calls, month, new Date(), Number(draft) || 0),
    [calls, month, draft]
  );
  const history = useMemo(() => {
    void saved;
    void version;
    return goalHistory(calls);
  }, [calls, saved, version]);

  async function onSave(e) {
    e.preventDefault();
    try {
      await saveGoal(draft, month);
    } catch {
      setMonthlyGoal(draft, month);
    }
    setSaved((n) => n + 1);
  }

  return (
    <div className="mpd-page">
      <div className="mpd-page-head">
        <div>
          <h1>Goals</h1>
          <p className="mpd-sub">{snapshot.monthLabel} income target</p>
        </div>
      </div>

      <section className="mpd-card">
        <form className="mpd-goal-form mpd-goal-form-inline" onSubmit={onSave}>
          <label>
            Month
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value || monthKey(new Date()))}
            />
          </label>
          <label>
            Monthly income target
            <input
              type="number"
              min="0"
              step="50"
              value={draft}
              placeholder="1000"
              onChange={(e) => setDraft(e.target.value)}
            />
          </label>
          <button className="mpd-primary" type="submit">Save goal</button>
        </form>
        {Number(draft) > 0 ? (
          <MonthlyGoalPanel snapshot={snapshot} symbol={symbol} />
        ) : (
          <p className="mpd-muted">
            Save a target for this month. Estimated earnings, progress, and daily pace use every call logged in {snapshot.monthLabel}.
          </p>
        )}
      </section>

      <section className="mpd-card mpd-table-card">
        <div className="mpd-card-head">
          <h2>Goal history</h2>
          <p>Saved monthly targets stay attached to that month</p>
        </div>
        {history.length === 0 ? (
          <p className="mpd-muted">No saved monthly goals yet.</p>
        ) : (
          <div className="mpd-goal-history-wrap">
            <table className="mpd-goal-history">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Target</th>
                  <th>Earned</th>
                  <th>Progress</th>
                  <th>Reached</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.month}>
                    <td>
                      <button type="button" className="mpd-goal-history-link" onClick={() => setMonth(row.month)}>
                        {row.monthLabel}
                      </button>
                    </td>
                    <td>{money(row.goal, symbol)}</td>
                    <td>{money(row.earned, symbol)}</td>
                    <td>
                      {(row.progressPct || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })}%
                    </td>
                    <td className={row.reached ? "is-up" : "is-down"}>{row.reached ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
